import React, { useState, useEffect, useCallback } from 'react';
import './GloriousIncompetence.css';

const DEFAULT_BATCH = 20;
const LOAD_MORE_THRESHOLD = 300;

function GloriousIncompetence({ theme, onBackClick }) {
    const [entries, setEntries] = useState([]);
    const [visibleCount, setVisibleCount] = useState(DEFAULT_BATCH);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;

        const loadEntries = async () => {
            try {
                const response = await fetch('/glorious-incompetence.json');
                if (!response.ok) {
                    throw new Error(`Unable to load content: ${response.status}`);
                }
                const data = await response.json();
                if (!cancelled) {
                    setEntries(Array.isArray(data) ? data : []);
                    setVisibleCount(DEFAULT_BATCH);
                }
            } catch (err) {
                if (!cancelled) {
                    console.error(err);
                    setError('Could not load the content. Please try again later.');
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        loadEntries();
        return () => {
            cancelled = true;
        };
    }, []);

    const handleScroll = useCallback(() => {
        if (isLoading || entries.length <= visibleCount) return;
        if (window.innerHeight + window.scrollY >= document.body.offsetHeight - LOAD_MORE_THRESHOLD) {
            setVisibleCount((prev) => Math.min(entries.length, prev + DEFAULT_BATCH));
        }
    }, [entries.length, isLoading, visibleCount]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    const visibleEntries = entries.slice(0, visibleCount);

    return (
        <div className={`app ${theme}`}>
            <main>
                <section className="incompetence-header-section">
                    <div className="incompetence-header">
                        <div>
                            <h1>Glorious Incompetence</h1>
                            <p className="incompetence-subtitle">
                                A hall of fame of glorious incompetence and malicious compliance.
                                We all know someone who deserves to be here. Maybe even yourself? (No judgment, we all have our moments.)
                            </p>
                        </div>
                        <button className="back-btn" onClick={onBackClick}>
                            Back to Home
                        </button>
                    </div>
                </section>

                <section className="incompetence-content">
                    {isLoading ? (
                        <div className="incompetence-status">Loading content…</div>
                    ) : error ? (
                        <div className="incompetence-status incompetence-error">{error}</div>
                    ) : (
                        <>
                            <div className="incompetence-list">
                                {visibleEntries.map((entry, index) => (
                                    entry.type === 'section' ? (
                                        <div key={`section-${index}`} className="incompetence-section-heading">
                                            {entry.text}
                                        </div>
                                    ) : (
                                        <article key={`entry-${index}`} className="incompetence-card">
                                            <span className="incompetence-index">{index + 1}.</span>
                                            <p>{entry.text}</p>
                                        </article>
                                    )
                                ))}
                            </div>

                            <div className="incompetence-footer-note">
                                {visibleCount < entries.length
                                    ? 'Scroll to load more stories...'
                                    : 'You have reached the end of glorious incompetence.'}
                            </div>
                        </>
                    )}
                </section>
            </main>

            <footer className="footer">
                <p>© 2026 StupidDesigns.com. All rights reserved. Not that anyone actually wants them.</p>
            </footer>
        </div>
    );
}

export default GloriousIncompetence;
